import { IAnswer } from '../../models/reflist/reflist.types';

/* Common Reflist types */

export enum EReflistChapterName {
  SEARCH = 'search',
  VISION = 'vision',
  PERSONALITY = 'personality',
  TASTES = 'tastes',
  VALUES = 'values',
  PERSONAL_DATA = 'personal-data',
}

export enum EQuestionType {
  BULLET = 'Bullet',
  LIST = 'List',
  MULTIPLE = 'MultipleChoice',
  RANGE = 'Range',
  SEARCH = 'Search',
  SLIDER = 'Slider',
  INFLUENCE = 'Influence',
}

interface IQuestionBase {
  type: EQuestionType;
  label: string;
  notice?: string;
  withValidation?: boolean;
  hasVisuals?: boolean;
}

interface IDefaultQuestions extends IQuestionBase {
  type: EQuestionType.BULLET | EQuestionType.INFLUENCE | EQuestionType.LIST;
}

interface IMultipleQuestion {
  type: EQuestionType.MULTIPLE;
  maxChoice?: number;
}
interface IRangeQuestion {
  type: EQuestionType.RANGE;
  initialValues: [number, number];
  stepValues: number[];
  minDifference: number;
  unit: string;
}
interface ISliderQuestion {
  stepValues: number[];
  type: EQuestionType.SLIDER;
  initialValue: number;
  unit: string;
}

interface ISearchQuestion {
  type: EQuestionType.SEARCH;
  /* Id of the "other" answer, if there is one */
  questionId?: number;
}

/* Dry Reflist types */

export type TDryDefaultReflistQuestion = IQuestionBase & IDefaultQuestions;
export type TDryMultipleReflistQuestion = IQuestionBase & IMultipleQuestion;
export type TDryRangeReflistQuestion = IQuestionBase & IRangeQuestion;
export type TDrySliderReflistQuestion = IQuestionBase & ISliderQuestion;
export type TDrySearchReflistQuestion = IQuestionBase &
  ISearchQuestion & {
    defaultAnswers?: () => number[];
  };

export type IDryReflistQuestion =
  | TDryDefaultReflistQuestion
  | TDryMultipleReflistQuestion
  | TDryRangeReflistQuestion
  | TDrySliderReflistQuestion
  | TDrySearchReflistQuestion;

/**
 * Dry reflist structure without answers from API and translations
 */
export type IDryReflist = Record<EReflistChapterName, IDryReflistQuestion[][]>;

/* Hydrated Reflist types */

export interface IHydratedQuestionBase extends IQuestionBase {
  title: string;
  subtitle?: string;
  // Answers is optional now because answers can be empty when getting them from back
  // https://gitlab.meetic.ilius.net/front-web-public/js-affiny/merge_requests/1663
  answers?: IAnswer[];
  defaultAnswers?: number[];
}

export type TDefaultReflistQuestion = IHydratedQuestionBase & IDefaultQuestions;
export type TMultipleReflistQuestion = IHydratedQuestionBase &
  IMultipleQuestion;
export type TRangeReflistQuestion = IHydratedQuestionBase & IRangeQuestion;
export type TSliderReflistQuestion = IHydratedQuestionBase & ISliderQuestion;
export type TSearchReflistQuestion = IHydratedQuestionBase &
  ISearchQuestion & {
    defaultAnswers?: number[];
  };

export type TReflistQuestion =
  | TDefaultReflistQuestion
  | TMultipleReflistQuestion
  | TRangeReflistQuestion
  | TSearchReflistQuestion
  | TSliderReflistQuestion;

export interface IReflistChapter {
  questions: TReflistQuestion[][];
}

/**
 * Hydrated reflist with answers from API and translations
 */
export type IHydratedReflist = Partial<
  Record<EReflistChapterName, IReflistChapter>
>;
